import React, { useState, useEffect } from 'react';
import { DemoScenario, DemoSeedResult } from '../types';
import { api } from '../services/api';
import './DemoControls.css';

interface DemoControlsProps {
  onDataUpdated?: () => void;
}

const DemoControls: React.FC<DemoControlsProps> = ({ onDataUpdated }) => {
  const [scenarios, setScenarios] = useState<DemoScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seedingScenario, setSeedingScenario] = useState<string | null>(null);
  const [seedResult, setSeedResult] = useState<DemoSeedResult | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearingData, setClearingData] = useState(false);

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getDemoScenarios();
      setScenarios(data);
    } catch (err) {
      setError('Failed to load demo scenarios. Please check your connection.');
      console.error('Error loading scenarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedScenario = async (scenarioKey: string, clearExisting: boolean = false) => {
    try {
      setSeedingScenario(scenarioKey);
      setSeedResult(null);
      setError(null);

      const result = await api.seedDemoScenario(scenarioKey, clearExisting);
      setSeedResult(result);

      // Call the callback to refresh data in parent
      if (onDataUpdated) {
        setTimeout(onDataUpdated, 500);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to seed scenario. Please try again.');
      console.error('Error seeding scenario:', err);
    } finally {
      setSeedingScenario(null);
    }
  };

  const handleClearData = async () => {
    try {
      setClearingData(true);
      setError(null);
      setSeedResult(null);

      await api.clearDemoData();
      setShowClearConfirm(false);

      // Call the callback to refresh data in parent
      if (onDataUpdated) {
        setTimeout(onDataUpdated, 500);
      }

      alert('All demo data cleared successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear demo data. Please try again.');
      console.error('Error clearing data:', err);
    } finally {
      setClearingData(false);
    }
  };

  const getPriorityBadgeClass = (count: number, type: 'RED' | 'YELLOW' | 'BLUE') => {
    if (count === 0) return `priority-badge priority-${type.toLowerCase()} zero`;
    return `priority-badge priority-${type.toLowerCase()}`;
  };

  if (loading) {
    return (
      <div className="demo-controls">
        <div className="demo-loading">
          <div className="spinner"></div>
          <p>Loading demo scenarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-controls">
      <div className="demo-header">
        <h1>Demo Scenario Controls</h1>
        <p className="demo-subtitle">
          Seed curated demo scenarios to explore ClarityHub features with realistic data
        </p>
      </div>

      {error && (
        <div className="demo-error">
          <span className="error-icon">⚠</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {seedResult && (
        <div className="demo-success">
          <div className="success-header">
            <span className="success-icon">✓</span>
            <h3>{seedResult.scenario}</h3>
          </div>
          <p className="success-description">{seedResult.description}</p>
          <div className="success-stats">
            <div className="stat-item">
              <span className="stat-label">Messages Processed:</span>
              <span className="stat-value">{seedResult.messages_processed}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Actionable Messages:</span>
              <span className="stat-value">{seedResult.actionable_messages}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Tasks Extracted by AI:</span>
              <span className="stat-value">{seedResult.tasks_extracted}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">AI Priority Distribution:</span>
              <div className="priority-mix">
                <span className={getPriorityBadgeClass(seedResult.priority_breakdown.RED, 'RED')}>
                  {seedResult.priority_breakdown.RED} RED
                </span>
                <span className={getPriorityBadgeClass(seedResult.priority_breakdown.YELLOW, 'YELLOW')}>
                  {seedResult.priority_breakdown.YELLOW} YELLOW
                </span>
                <span className={getPriorityBadgeClass(seedResult.priority_breakdown.BLUE, 'BLUE')}>
                  {seedResult.priority_breakdown.BLUE} BLUE
                </span>
              </div>
            </div>
          </div>
          <button onClick={() => setSeedResult(null)} className="success-close-btn">
            Dismiss
          </button>
        </div>
      )}

      <div className="scenarios-grid">
        {scenarios.map((scenario) => (
          <div key={scenario.key} className="scenario-card">
            <h3 className="scenario-name">{scenario.name}</h3>
            <p className="scenario-description">{scenario.description}</p>

            <div className="scenario-priority-mix">
              <span className="mix-label">Priority Mix:</span>
              <div className="priority-badges">
                <span className={getPriorityBadgeClass(scenario.priority_mix.RED, 'RED')}>
                  {scenario.priority_mix.RED} RED
                </span>
                <span className={getPriorityBadgeClass(scenario.priority_mix.YELLOW, 'YELLOW')}>
                  {scenario.priority_mix.YELLOW} YELLOW
                </span>
                <span className={getPriorityBadgeClass(scenario.priority_mix.BLUE, 'BLUE')}>
                  {scenario.priority_mix.BLUE} BLUE
                </span>
              </div>
            </div>

            <div className="scenario-actions">
              <button
                onClick={() => handleSeedScenario(scenario.key, false)}
                disabled={seedingScenario !== null}
                className="btn-seed"
              >
                {seedingScenario === scenario.key ? (
                  <>
                    <span className="btn-spinner"></span>
                    Seeding...
                  </>
                ) : (
                  'Seed Scenario'
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="demo-footer">
        <div className="footer-actions">
          <button
            onClick={() => setShowClearConfirm(true)}
            disabled={clearingData}
            className="btn-clear-all"
          >
            {clearingData ? 'Clearing...' : 'Clear All Demo Data'}
          </button>
        </div>

        <div className="footer-note">
          <p>
            <strong>Note:</strong> Demo scenarios inject realistic Slack messages that are processed
            through the FULL AI pipeline (NoiseFilter → TaskExtractor → Supervisor → PriorityEngine).
            Priority distributions are determined naturally by the AI, demonstrating real capabilities.
          </p>
        </div>
      </div>

      {showClearConfirm && (
        <div className="modal-overlay" onClick={() => setShowClearConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Clear All Demo Data?</h3>
            <p>
              This will remove all tasks that were created via demo scenarios. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                onClick={handleClearData}
                disabled={clearingData}
                className="btn-confirm-clear"
              >
                {clearingData ? 'Clearing...' : 'Yes, Clear All'}
              </button>
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearingData}
                className="btn-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoControls;
